import React, { useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'app/store';
import {
  createSharedPlaylistLinkEffect,
  getSharedPlaylistsEffect,
  revokeSharedPlaylistLinkEffect,
  updateSharedPlaylistLinkEffect,
} from 'app/store/Shares/effects';
import {
  getSharedPlaylistLinks,
  getSharedLinkListNextLink,
} from 'app/store/Shares/selectors';
import { Link } from 'types/api.types';
import { ShareLinkDialog } from 'app/components/Shares/LinkDialog';
import { useIsMounted } from 'app/utils/hooks';
import { ApiErrorCodeTypes } from 'types/enums';
import { setShowUpgradePopup } from 'app/store/Storage/slice';
import { UpdateSharedLinkEffectData } from 'app/store/Shares/effects.types';
import { previewSharedPlaylistAction } from 'app/store/Mixpanel/slice';
import { useShowUpgradePopup } from './utils';

interface PlaylistShareLinkDialogProps {
  isOpen: boolean;
  onClose: () => void;
  playlistTitle: string;
  playlistGuid: string;
}

export const PlaylistShareLinkDialog: React.FC<PlaylistShareLinkDialogProps> = ({
  isOpen,
  playlistTitle,
  onClose,
  playlistGuid,
}) => {
  const isMounted = useIsMounted();
  const dispatch = useAppDispatch();
  const {
    updateShareLinkAllowed,
    revokeAllowed,
    showUpgradePopupForRevokeShareLink,
    showUpgradePopupForUpdateShareLink,
  } = useShowUpgradePopup({
    playlistGuid,
  });

  const links = useAppSelector((state) =>
    getSharedPlaylistLinks(state, playlistGuid)
  );
  const [isLoading, setIsLoading] = useState(false);

  const addLink = async (title: string) => {
    try {
      return await dispatch(
        createSharedPlaylistLinkEffect({ title, playlistGuid })
      ).unwrap();
    } catch (e: any) {
      if (e?.errorCode === ApiErrorCodeTypes.LIMIT_REACHED) {
        dispatch(
          setShowUpgradePopup({
            messageType: 'playlist',
            context: { action: 'createPlaylistShareLink', playlistGuid },
          })
        );
      }
      throw e;
    }
  };

  const revokeLink = async (linkGuid: string, link: Link) => {
    if (!revokeAllowed()) {
      return;
    }

    try {
      await dispatch(
        revokeSharedPlaylistLinkEffect({ linkGuid, link, guid: playlistGuid })
      ).unwrap();
    } catch (e: any) {
      if (e?.errorCode === ApiErrorCodeTypes.LIMIT_REACHED) {
        showUpgradePopupForRevokeShareLink();
      }
    }
  };

  const updateSharedPlaylistLink = async (
    link: Link,
    data: UpdateSharedLinkEffectData['data'],
    linkGuid?: string
  ) => {
    if (!updateShareLinkAllowed(data)) {
      return Promise.resolve();
    }

    try {
      return await dispatch(
        updateSharedPlaylistLinkEffect({
          link,
          guid: playlistGuid,
          data,
          linkGuid,
        })
      ).unwrap();
    } catch (e: any) {
      if (e?.errorCode === ApiErrorCodeTypes.LIMIT_REACHED) {
        showUpgradePopupForUpdateShareLink(data);
      }
      throw e;
    }
  };

  const fetchSharedPlaylistLinks = async () => {
    try {
      setIsLoading(true);
      await dispatch(getSharedPlaylistsEffect({ guid: playlistGuid })).unwrap();
    } catch {
      // proceed
    } finally {
      if (isMounted()) {
        setIsLoading(false);
      }
    }
  };

  useEffect(() => {
    if (isOpen) {
      fetchSharedPlaylistLinks();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  return (
    <ShareLinkDialog
      isOpen={isOpen}
      onClose={onClose}
      title={playlistTitle}
      addLink={addLink}
      revokeLink={revokeLink}
      updateSharedLink={updateSharedPlaylistLink}
      sharedLinks={links || []}
      isLoading={isLoading}
      guid={playlistGuid}
      getSharedListEffect={getSharedPlaylistsEffect}
      getSharedLinkListNextLink={getSharedLinkListNextLink}
      playlist
      onPreviewClick={({
        guid: playlistGuid,
        shareCount,
        creditsShared,
        lyricsShared,
        downloadable,
      }) => {
        dispatch(
          previewSharedPlaylistAction({
            playlistGuid,
            shareCount,
            creditsShared,
            lyricsShared,
            downloadable,
          })
        );
      }}
    />
  );
};
